import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { ApiUserMaintenance } from '../../../api/core/api-user-maintenance';
import { ApiSiteMaintenance } from '../../../api/core/api-site-maintenance';
import { toErrorMessage, toErrorNotice } from '../../../api/error-utils';
import { KeptText } from '../../../language/kept-text-value';
import type { ApiResponse } from '../../../api/agwasuite-api';
import { getLockStore } from '../../../utility/locking/lock-store';
import { isUnanswered } from '../../../api/unanswered-request';
import { COMPANY_LOCK_RESOURCE } from '../shared/company-lock';
import { SecurityApi } from '../../../api/security/security-api';
import type {
  LoginGroup,
  LoginGroupImis,
  LoginSecurityContext,
} from '../../../api/agwa-api-model';
import { UserProfile } from '../../../datatypes/user-profile';
import { terms } from '../../../language/terms';

export interface MemberWorkspaceState {
  loading: boolean;
  error: string | null;
  group: LoginGroup | null;
  imis: LoginGroupImis | null;
  capabilityProfile: UserProfile | null;
  securityError: string | null;
  /**
   * Operation-type codes the company holds by inheritance from its active sites. Gates the
   * company-level tabs of site-scoped modules (glass certificates). Empty until the sites
   * load, and empty if they cannot: a tab that fails to gate is hidden, never shown wrongly.
   */
  inheritedDomains: string[];
}

/**
 * The answer to a save or a block of the company: done, or refused with the server's reason in words
 * (#406, @LOCK-065), so the page can say why rather than a generic failure. `errorKept` is that same
 * reason as the server sent it, tags intact, for the error toast to resolve once and show a named
 * person by their current name (@LOCK-068, @VOC-SERVER-002); absent when the reason is the store's
 * own sentence. `unanswered` marks a request that got no answer at all (D-144): the connection
 * monitor already tells the person, so the page presents nothing of its own for it.
 */
export type MemberWorkspaceWriteResult =
  | { ok: true }
  | { ok: false; error: string; errorKept?: KeptText; unanswered?: true };

/** The server's reason as kept text, when that is what the response's notice is (toErrorNotice). */
function keptReason<T>(response: ApiResponse<T>): { errorKept?: KeptText } {
  const notice = toErrorNotice(response);
  return notice instanceof KeptText ? { errorKept: notice } : {};
}

const DEFAULT_STATE: MemberWorkspaceState = {
  loading: false,
  error: null,
  group: null,
  imis: null,
  capabilityProfile: null,
  securityError: null,
  inheritedDomains: [],
};

export class MemberWorkspaceStore extends EventTarget {
  readonly groupId: string;
  private state: MemberWorkspaceState = { ...DEFAULT_STATE };
  private groupLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  constructor(groupId: string) {
    super();
    this.groupId = groupId;
  }

  get snapshot(): MemberWorkspaceState {
    return this.state;
  }

  async ensureHydrated() {
    if (this.groupLoaded || this.loadingPromise) {
      return this.loadingPromise ?? Promise.resolve();
    }
    this.loadingPromise = this.fetchGroupBundle();
    try {
      await this.loadingPromise;
    } finally {
      this.loadingPromise = null;
    }
  }

  /**
   * Reads the company again, for a page that learns somebody else has just finished editing it
   * (their edit lock released, #398 C11, @LOCK-034), so it shows the saved values without a reload.
   */
  async reload() {
    if (this.loadingPromise) {
      await this.loadingPromise;
    }
    this.groupLoaded = false;
    await this.ensureHydrated();
  }

  async refreshSecurity() {
    if (!this.state.group) {
      await this.ensureHydrated();
      if (!this.state.group) return;
    }
    await this.loadSecurityProfile(this.state.group);
  }

  /**
   * Blocks or unblocks the company through its own command (#406, @LOCK-066), which changes only
   * whether it is active. The server takes the company's edit lock for this session and releases it
   * after (@LOCK-038, @LOCK-070), so that release is announced to the lock store first and the page
   * does not read its own block as a lost lock. A refusal answers the server's reason, which the API
   * client has already put into words, and beside it the reason as sent, whose holder is a person tag
   * the toast shows as their current name (@LOCK-065, @LOCK-068, @VOC-SERVER-002).
   */
  async setActive(active: boolean): Promise<MemberWorkspaceWriteResult> {
    const group = this.state.group;
    if (!group) {
      return { ok: false, error: `No ${terms.loginGroup.lower} is loaded.` };
    }
    const lockStore = getLockStore();
    lockStore.expectServerRelease(COMPANY_LOCK_RESOURCE, group.id);
    try {
      const api = ApiFactory.authenticated(ApiUserMaintenance);
      const response = await api.setGroupBlocked({
        loginGroupId: group.id,
        blocked: !active,
      });
      if (!response.success || !response.data?.group) {
        // Refused: the server took no lock, so no release is coming. A request that got no answer
        // (D-144) may still have been carried out, release and all, so its announcement is left to
        // lapse at its bound rather than withdrawn: withdrawn, the page would read its own block's
        // release as a lost lock (#406 u3 review 1, F5).
        const unanswered = isUnanswered(response);
        if (!unanswered) {
          lockStore.cancelExpectedRelease(COMPANY_LOCK_RESOURCE, group.id);
        }
        return {
          ok: false,
          error:
            toErrorMessage(response) ||
            `Unable to ${active ? 'unblock' : 'block'} the ${terms.loginGroup.lower}.`,
          ...keptReason(response),
          ...(unanswered ? { unanswered: true as const } : {}),
        };
      }
      this.updateState({ group: response.data.group });
      return { ok: true };
    } catch (error) {
      lockStore.cancelExpectedRelease(COMPANY_LOCK_RESOURCE, group.id);
      console.error('Failed to update member active status:', error);
      return {
        ok: false,
        error: `Unable to ${active ? 'unblock' : 'block'} the ${terms.loginGroup.lower}.`,
      };
    }
  }

  /**
   * Saves the company's details. A refusal answers the server's reason, which the API client has
   * already put into words (#406, @LOCK-065).
   * Note: userNamePrefix cannot be changed after creation, and whether the company is active is not
   * changed here (setActive, @LOCK-069).
   */
  async saveGroup(
    updates: Partial<LoginGroup>
  ): Promise<MemberWorkspaceWriteResult> {
    const group = this.state.group;
    if (!group) {
      return { ok: false, error: `No ${terms.loginGroup.lower} is loaded.` };
    }
    try {
      const api = ApiFactory.authenticated(ApiUserMaintenance);
      const updatedGroup: LoginGroup = {
        ...group,
        name: updates.name ?? group.name,
        // userNamePrefix cannot be changed - always use existing value. Active is sent as stored;
        // the server ignores it on a details save (@LOCK-069).
        active: group.active,
        // Membership is correctable after creation: a company joins, or lapses, and the register has
        // to be able to say so without the company being re-entered.
        membershipStatus: updates.membershipStatus ?? group.membershipStatus,
      };
      const response = await api.editGroup(updatedGroup);
      if (!response.success || !response.data?.group) {
        return {
          ok: false,
          error:
            toErrorMessage(response) ||
            `Unable to save ${terms.loginGroup.lower} details.`,
          ...keptReason(response),
          ...(isUnanswered(response) ? { unanswered: true as const } : {}),
        };
      }
      this.updateState({ group: response.data.group });
      return { ok: true };
    } catch (error) {
      console.error('Failed to save member details:', error);
      return {
        ok: false,
        error: `Unable to save ${terms.loginGroup.lower} details.`,
      };
    }
  }

  private async fetchGroupBundle() {
    this.updateState({ loading: true, error: null });
    try {
      const api = ApiFactory.authenticated(ApiUserMaintenance);
      const response = await api.getGroups();
      if (!response.success || !response.data?.groups) {
        throw new Error(
          response.error || `Unable to load ${terms.loginGroup.lower} details.`
        );
      }
      const group =
        response.data.groups.find((g) => g.id === this.groupId) ?? null;
      if (!group) {
        throw new Error(`${terms.loginGroup.value} not found.`);
      }
      const imisMatch =
        response.data.imis?.find((i) => i.loginGroupId === this.groupId) ??
        null;
      this.groupLoaded = true;
      // The security profile and the sites are independent reads; neither waits on the other.
      const [, inheritedDomains] = await Promise.all([
        this.loadSecurityProfile(group),
        this.loadInheritedDomains(),
      ]);
      this.updateState({
        loading: false,
        group,
        imis: imisMatch,
        inheritedDomains,
        error: null,
      });
    } catch (error) {
      this.updateState({
        loading: false,
        error:
          (error as Error)?.message ??
          `Unable to load ${terms.loginGroup.lower} details.`,
        group: null,
        imis: null,
        capabilityProfile: null,
        inheritedDomains: [],
      });
    }
  }

  /**
   * The union of the company's ACTIVE sites' operation-type codes - what the company is by
   * inheritance, the same reading the overview's operation-types section makes. A failed read
   * yields no codes rather than an error: the workspace still opens, and the site-scoped
   * tabs simply do not appear.
   */
  private async loadInheritedDomains(): Promise<string[]> {
    try {
      const api = ApiFactory.authenticated(ApiSiteMaintenance);
      const response = await api.getMemberSites(this.groupId);
      if (!response.success) return [];
      const codes = new Set<string>();
      for (const site of response.data?.items ?? []) {
        if (!site.active) continue;
        for (const code of site.organizationTypes ?? []) codes.add(code);
      }
      return [...codes];
    } catch (error) {
      console.error('Failed to load the member sites for inheritance:', error);
      return [];
    }
  }

  private async loadSecurityProfile(group: LoginGroup) {
    try {
      const securityApi = ApiFactory.authenticated(SecurityApi);
      const response = await securityApi.getGroupRoleSummary(group.id);
      if (!response.success) {
        throw new Error(response.error || 'Unable to load role defaults.');
      }
      const capabilityProfile = this.buildProfile(group, response.data);
      this.updateState({
        capabilityProfile,
        securityError: null,
      });
    } catch (error) {
      this.updateState({
        capabilityProfile: this.buildProfile(group),
        securityError:
          (error as Error)?.message ?? 'Unable to load role defaults.',
      });
    }
  }

  private buildProfile(
    group: LoginGroup,
    securityContext?: LoginSecurityContext | null
  ) {
    return new UserProfile({
      authProvider: 'internal',
      username: group.userNamePrefix || group.name,
      displayName: `${group.name} defaults`,
      emailAddress: undefined,
      claims: {}, // Claims are now derived from securityContext, not stored on group
      securityContext: securityContext ?? undefined,
    });
  }

  private updateState(partial: Partial<MemberWorkspaceState>) {
    this.state = {
      ...this.state,
      ...partial,
    };
    this.dispatchEvent(new Event('change'));
  }
}

const storeRegistry = new Map<string, MemberWorkspaceStore>();

export function getMemberWorkspaceStore(groupId: string) {
  let store = storeRegistry.get(groupId);
  if (!store) {
    store = new MemberWorkspaceStore(groupId);
    storeRegistry.set(groupId, store);
  }
  return store;
}
